<template>
	<v-skeleton-loader v-if="skeleton" type="list-item,list-item,list-item" />
	<v-sheet v-else data-test-selector="adyen_form">

		<div v-if="_loading" class="d-flex justify-center">
			<v-progress-linear indeterminate />
		</div>

		<!-- Dropin container with transparent overlay for blocking interactions -->
		<div class="dropin-container" :class="{ 'interaction-blocked': !canPay && acceptConditionsFeatureEnabled }">
			<div data-testid="adyenForm" id="adyen_form_container" v-show="dropinElement"></div>

			<!-- Transparent overlay to block interactions when requirements not met -->
			<div v-if="!canPay && acceptConditionsFeatureEnabled" class="transparent-overlay" @click="handleOverlayClick">
			</div>
		</div>

		<v-checkbox
			data-testid="agreeTnc"
			v-on:click="unlockCardPayments"
			v-model="readTerms"
			:disabled="paying || readTerms"
			name="read_terms"
			:rules="[rules.required]"
			:class="{
				'highlight-error-blue': showTermsError && acceptConditionsFeatureEnabled,
				'highlight-error': showTermsError && !acceptConditionsFeatureEnabled,
				'mb-1': true
			}"
		>
			<template v-slot:label>
				<span>
					<span v-text="$t('paymentForm.creditBalanceDialog.readTerms')"></span>
					&nbsp;<a @click.prevent.stop="handleTermsClick" v-text="$t('paymentForm.creditBalanceDialog.terms')"></a>
				</span>
			</template>
		</v-checkbox>

		<div class="g-recaptcha"
			v-if="enableCaptcha"
			data-callback="onReCaptchaSuccess"
			:data-sitekey="$root.websiteConfig.data.gcp_recaptcha"
			data-action="PAYMENT"
			:class="{
				'highlight-error-blue': showCaptchaError && acceptConditionsFeatureEnabled,
				'highlight-error': showCaptchaError && !acceptConditionsFeatureEnabled
			}"
		></div>
	</v-sheet>
</template>

<script>
import { AdyenCheckout, Dropin } from '@adyen/adyen-web/auto';
import '@adyen/adyen-web/styles/adyen.css';
import { PaymentService, EventBus, CartModel, BaseModel, Rules, EComService } from '@connectngo/sdk';
import CaptchaMixin from "@/mixins/CaptchaMixin";

export default {
	name: 'AdyenForm',

	mixins: [CaptchaMixin],

	props: {
		cart: {
			type: CartModel,
			default: () => new CartModel(),
		},
		engine: {
			type: BaseModel,
			default: () => new BaseModel(),
		},
		skeleton: {
			type: Boolean,
			default: false,
		},
		balance: {
			type: Number,
			default: null
		}
	},

	data: () => ({
		loading: false,
		paying: false,
		success: false,
		error: false,
		readTerms: false,
		dropinElement: null,
		dropInData: null,
		redirectUrl: null,
		paymentLockSeconds: 0,
		adyenButton: null,
		captchaReady: false,
		showTermsError: false,
		showCaptchaError: false,
		dropinRetryCount: 0,
		callbackInProgress: false,
		KLARNA_PAYMENT_TYPES: Object.freeze([
			'klarna',
			'klarna_paynow',
			'klarna_account',
			'klarna_paylater',
		]),
		RESERVATION_EXCLUDED_TYPES: Object.freeze([
			'klarna',
			'klarna_paynow',
			'klarna_account',
			'klarna_paylater',
			'applepay',
			'googlepay',
		]),
	}),

	computed: {
		_loading() {
			return this.loading && !this.dropinElement;
		},
		canPay() {
			const captchaCheck = this.enableCaptcha ? this.captchaReady : true;
			return !this.paying
				&& !this.loading
				&& this.readTerms
				&& captchaCheck;
		},
		acceptConditionsFeatureEnabled() {
			const { feature_flags } = this.$root?.websiteConfig?.data || {};
			return !!(feature_flags && feature_flags.includes('co-10241-accept-the-conditions'))
		},
		klarnaFeatureFlagEnabled() {
			const { feature_flags } = this.$root?.websiteConfig?.data || {};
			return feature_flags && feature_flags.includes('co-9632-klarna-payment');
		},
		isReservation() {
			return !!this.cart.data.reservation_id;
		},
		isCorporateAccount() {
			return !!this.cart.data.account?.salesgroup_id;
		},
		ancvAydenPaymentIssueFixFeatureFlagEnabled() {
			const { feature_flags } = this.$root?.websiteConfig?.data || {};
			return feature_flags && feature_flags.includes('co-11120-adyen-issue-with-ancv-partial-payment-fix');
		},
		dev11517RedirectFlowFixFfEnabled() {
			const { feature_flags } = this.$root?.websiteConfig?.data || {};
			return !!(feature_flags && feature_flags.includes('dev-11517-fix-redirect-flow-for-ecom-payments'));
		},
	},

	methods: {
		/**
		 * Filters out payment methods by their types
		 * @param {Object} paymentMethodsResponse - The payment methods response object
		 * @param {Set<string>} excludedTypes - Set of payment method types to exclude
		 * @returns {Object} Filtered payment methods response
		 */
		filterPaymentMethods(paymentMethodsResponse, excludedTypes) {
			return {
				...paymentMethodsResponse,
				paymentMethods: paymentMethodsResponse.paymentMethods.filter(
					pm => !excludedTypes.has(pm.type)
				)
			};
		},

		handleOverlayClick() {
			if (!this.readTerms) {
				if (this.acceptConditionsFeatureEnabled) {
					this.$snack(
						this.$t('paymentForm.creditBalanceDialog.terms.error'),
						'#2196F3',
						'mdi-alert'
					);
				} else {
					this.$handleError(this, {
						message: this.$t('paymentForm.creditBalanceDialog.terms.error')
					});
					this.showTermsError = true;
				}
			} else if (this.enableCaptcha && !this.captchaReady) {
				if (this.acceptConditionsFeatureEnabled) {
					this.$snack(
						this.$t('paymentForm.creditBalanceDialog.captcha.error'),
						'#2196F3',
						'mdi-alert'
					);
				} else {
					this.$handleError(this, {
						message: this.$t('paymentForm.creditBalanceDialog.captcha.error')
					});
					this.showCaptchaError = true;
				}
			}
		},

		onCaptchaSuccess() {
			this.captchaReady = true;
			this.unlockCardPayments();
		},

		handleTermsClick() {
			EventBus.publish('OPEN_PAGE', 'terms');
		},

		async loadDropIn() {
			this.loading = true;
			let amount = this.balance || this.cart.data.total;

			if (this.ancvAydenPaymentIssueFixFeatureFlagEnabled) {
				amount = this.balance || this.cart.data.amountToPay || this.cart.data.total;
			}

			if(this.cart.deposit){
				amount = this.cart.depositAmount;
			}

			if (this.dropinElement) {
				this.loading = false;
				return;
			}

			try {
				const response = await new PaymentService().getAdyenDropIn(
					this.engine.data.id,
					amount
				);

				this.dropInData = response.data;

				let paymentMethodsResponse = response.data.payment_methods;

				// Determine which payment methods to exclude
				const excludedTypes = new Set();

				// Exclude Klarna if feature flag is disabled or for corporate/guest accounts
				if (!this.klarnaFeatureFlagEnabled || this.isCorporateAccount) {
					this.KLARNA_PAYMENT_TYPES.forEach(type => excludedTypes.add(type));
				}

				// For reservations, exclude additional payment methods
				if (this.isReservation) {
					this.RESERVATION_EXCLUDED_TYPES.forEach(type => excludedTypes.add(type));
				}

				// Apply filters if any exclusions are needed
				if (excludedTypes.size > 0) {
					paymentMethodsResponse = this.filterPaymentMethods(paymentMethodsResponse, excludedTypes);
				}

				const checkout = await AdyenCheckout({
					paymentMethodsResponse: paymentMethodsResponse,
					clientKey: response.data.client_key,
					locale: response.data.locale,
					environment: response.data.environment,
					countryCode: response.data.countryCode,

					onSubmit: async (state, dropin, actions) => {
						this.showTermsError = false;
						this.showCaptchaError = false;
						if (!this.canPay) {
							if (!this.readTerms) {
								if (this.acceptConditionsFeatureEnabled) {
									this.$snack(
										this.$t('paymentForm.creditBalanceDialog.terms.error'),
										'#2196F3',
										'mdi-alert'
									);
								} else {
									this.$handleError(this, { message: this.$t('paymentForm.creditBalanceDialog.terms.error') });
								}
								this.showTermsError = true;
							} else {
								if (this.acceptConditionsFeatureEnabled) {
									this.$snack(
										this.$t('paymentForm.creditBalanceDialog.captcha.error'),
										'#2196F3',
										'mdi-alert'
									);
								} else {
									this.$handleError(this, { message: this.$t('paymentForm.creditBalanceDialog.captcha.error') });
								}
							}

							dropin.setStatus('ready');
							return;
						}

						this.pay(state, actions);
					},

					onError: (error) => {
						this.setDropInState('ready');
						console.error("Adyen error:", error);
					}
				});

				const dropinConfiguration = {
					openFirstPaymentMethod: paymentMethodsResponse.paymentMethods.length === 1,
					showStoredPaymentMethods: false,

					paymentMethodsConfiguration: (() => {
						const amount = {
							amount: {
								currency: response.data.currency,
								value: response.data.amount
							}
						};

						return {
							card: {
								hasHolderName: true,
								holderNameRequired: true,
								placeholders: {
									holderName: 'J. Smith',
									cardNumber: '1234 5678 9012 3456',
								},
								enableStoreDetails: false,
								billingAddressRequired: false
							},
							applepay: amount,
							googlepay: amount,
							klarna: amount,
							klarna_paynow: amount,
							klarna_account: amount,
							klarna_paylater: amount,
						};
					})()
				};

				const container = await this.waitForElement('#adyen_form_container');
				if (!container) return;

				this.dropinElement = new Dropin(checkout, dropinConfiguration).mount('#adyen_form_container');

			} catch (error) {
				this.$handleError(this, error);
			} finally {
				this.loading = false;
			}
		},


		async waitForElement(selector, maxRetries = 10, delay = 100) {
			let attempt = 0;

			while (attempt <= maxRetries) {
				const el = document.querySelector(selector);
				if (el) return el;

				if (attempt === maxRetries) {
					console.error(`Element ${selector} not found after ${maxRetries} retries.`);
					return null;
				}

				await new Promise(resolve => setTimeout(resolve, delay));
				attempt++;
			}

			return null;
		},

		setDropInState(state) {
			if (this.dropinElement) {
				this.dropinElement.setStatus(state);
			}
		},

		unlockCardPayments: function () {
			if (this.canPay) {
				this.setDropInState('ready');
				// Clear any error states when unlocking
				this.showTermsError = false;
				this.showCaptchaError = false;
			}
		},

		pay(state, actions) {
			// Use type for non-card payment methods (klarna, ideal, etc.) and brand for cards
			const paymentMethod = state.data.paymentMethod.brand || state.data.paymentMethod.type;

			this.paying = true;
			this.error = false;
			this.$emit('paymentOverlay', [this.paying, this.success, this.error]);

			if (this.dropinElement) {
				this.dropinElement.setStatus('loading');
			}

			new PaymentService().processAdyenPayment(
				this.engine.data.id,
				this.cart.data.uuid,
				state.data,
				this.redirectUrl,
				this.cart.data.amountToPay,
				this.cart.deposit
			)
			.then(response => {
				this.handlePaymentSuccess(response, paymentMethod);
				const resultCode = response.resultCode;
				actions.resolve({resultCode});
			})
			.catch(error => {
				this.handlePaymentError(error);
				actions.reject();
			})
		},

		handlePaymentSuccess(response, paymentMethod) {
			if (response.data.action) {
				if (this.dropinElement) {
					this.dropinElement.setStatus('ready');
					this.dropinElement.handleAction(response.data.action);
				}
			} else {
				this.$emit('completed', response);
			}
			this.paying = false;
			this.$emit('paymentOverlay', [this.paying, this.success, this.error]);

			if (paymentMethod) {
				const values = {
					paymentMethod: paymentMethod,
					amount: this.cart.data.amountToPay
				}
				this.$emit('paymentMethodCaptured', values)
			}
		},

		handlePaymentError(error) {
			if (this.$isAlreadyPaid(error)) {
				this.paying = false
				this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
				this.$emit('completed');
			}

			if (this.$isAlreadyLocked(error) && this.paymentLockSeconds < error.timeout) {
				this.checkPaymentLock();
			} else {
				// Check if this is a Klarna-specific error
				const isKlarnaError = error.response?.data?.paymentMethod?.type?.includes('klarna');

				if (isKlarnaError) {
					// Provide Klarna-specific error message
					this.$handleError(this, {
						message: this.$t('paymentForm.klarna.error.declined')
					});
				}

				this.loadCaptcha();
				this.paymentLockSeconds = 0;
				this.paying = false;
				this.error = true;
				if (this.dropinElement) {
					this.dropinElement.setStatus('error', error);
				}
				this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
				setTimeout(() => {
					if (this.dropinElement) {
						this.dropinElement.setStatus('ready');
					}
				}, 3000);
			}
		},

		checkPaymentLock() {
			return new EComService().getPaymentLockStatus(this.cart.data.uuid)
				.then(response => {
					this.paymentLockSeconds = 0;
					this.handlePaymentSuccess(response);
				})
				.catch(error => {
					this.paymentLockSeconds += 2;
					setTimeout(() => {
						this.paying = true;
						this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
						if (this.dropinElement) {
							this.dropinElement.setStatus('loading');
						}
						this.handlePaymentError(error);
					}, 2000)
				})
		},

		doCallback(paymentId, redirectResult) {
			if (this.dev11517RedirectFlowFixFfEnabled && this.callbackInProgress) {
				return;
			}

			if (this.dev11517RedirectFlowFixFfEnabled) {
				this.callbackInProgress = true;
			}

			new PaymentService().doAdyenCallback(paymentId, redirectResult)
				.then(response => {
					this.error = false;
					this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
					this.$emit('completed', response);
				})
				.catch(error => {
					// Reset the flag only when the callback has an error
					// the callback should be called only once if successfull
					if (this.dev11517RedirectFlowFixFfEnabled) {
						this.callbackInProgress = false;
					}

					this.error = true;
					this.$emit('paymentOverlay', [this.paying, this.success, this.error]);
					if (this.dropinElement) {
						this.dropinElement.setStatus('error', error);
					}
					setTimeout(() => {
						if (this.dropinElement) {
							this.dropinElement.setStatus('ready');
						}
					}, 3000);
				});
		},
	},

	watch: {
		canPay: {
			handler(newVal) {
				if (newVal && this.dropinElement) {
					// Enable selection when all validations are completed
					this.dropinElement.setStatus('ready');
					this.showTermsError = false;
					this.showCaptchaError = false;
				}
				// Emit event to parent when canPay changes
				this.$emit('canPayChanged', newVal);
			},
			immediate: true
		}
	},

	async created() {
		this.rules = {
			required: value => Rules.required(value) || this.$t('rules.required'),
			creditCardNumber: value => Rules.creditCardNumber(value) || this.$t('rules.creditCardNumber'),
			creditCardCvc: value => Rules.creditCardCvc(value) || this.$t('rules.creditCardCvc'),
			creditCardExp: value => Rules.creditCardExp(value) || this.$t('rules.creditCardExp'),
			zipPostal: value => Rules.zipCode(value) || Rules.postalCode(value) || this.$t('rules.creditCardZipPostal'),
		};

		const threeDsResolutionRoutes =  ['cart_payment_3ds', 'reservation_3ds'];
		if (threeDsResolutionRoutes.includes(this.$route.name)) {
			this.doCallback(this.$route.params.id, this.$route.query.redirectResult);
		}

		const reservationRoutes = ['reservation', 'reservation_3ds'];
		if (reservationRoutes.includes(this.$route.name)) {
			this.redirectUrl = window.location.origin + this.$router.resolve(
				{
					name: 'reservation_3ds_prepare',
					params: this.$route.params
				}
			).href;
		} else {
			this.redirectUrl = window.location.origin + this.$router.resolve({ name: 'cart_payment_3ds_prepare', params: { step: 'payment', engine: 'adyen_ecom' } }).href;
		}

		if (this.$route.query.redirectResult) {
			this.readTerms = true;
		}
	},

	mounted() {
		this.$nextTick(() => this.loadDropIn());
		this.$on('captcha-success', this.onCaptchaSuccess);
	},
}
</script>
<style scoped>
.highlight-error-blue {
	box-shadow: 0 0 0 1.5px #2196F3;
	border-radius: 4px;
	transition: box-shadow 0.3s;
}

.highlight-error {
	box-shadow: 0 0 0 2px #ff5252;
	border-radius: 4px;
	transition: box-shadow 0.3s;
}

/* Dropin container with overlay control */
.dropin-container {
	position: relative;
}

.interaction-blocked {
	pointer-events: none;
}

.interaction-blocked .transparent-overlay {
	pointer-events: auto;
}

/* Transparent overlay to block interactions */
.transparent-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	background: rgba(255, 255, 255, 0.7);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 10;
	border-radius: 8px;
	backdrop-filter: blur(0.5px);
	cursor: pointer;
	pointer-events: auto;
}

.overlay-message {
	text-align: center;
	padding: 24px;
	background: rgba(255, 255, 255, 0.95);
	border-radius: 12px;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
	max-width: 320px;
	border: 1px solid rgba(0, 0, 0, 0.1);
}

.overlay-message p {
	margin-top: 16px;
	color: #555;
	font-size: 14px;
	line-height: 1.5;
	font-weight: 500;
}

/* Ensure payment methods are visible but not interactive when blocked */
.interaction-blocked .adyen-checkout__payment-method {
	opacity: 0.8;
}
</style>
